import React, { useEffect, useRef, useState } from 'react';
import { BottomSheetProps } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import { StyledDiv } from '../../../Theme/CommonStyled';
import { StyledBottomSheet, StyledSheetContainer } from './style';
import { getQueryParams } from '../../../Utils/common.util';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';
import Button from '../Button';
import { useTranslation } from 'react-i18next';

type ActionSheetType = BottomSheetProps & {
  onApply?: () => void;
  onDefault?: () => void;
  loading?: boolean;
  id?: string;
};

const ActionSheet: React.FC<ActionSheetType> = ({
  children,
  onApply,
  onDefault,
  loading,
  id = 'user',
  ...rest
}) => {
  const [isOnDismissCalledStatus, setIsOnDismissCalledStatus] = useState(false);
  const bottomSheetRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isOpenActionSheet = getQueryParams(location.search)?.actionSheet;

  useEffect(() => {
    const operator = location.search.includes('?') ? '&' : '?';
    rest.open && navigate(`${location.search}${operator}actionSheet=${id}`);
    window.addEventListener('popstate', () => {
      rest.onDismiss();
      return true;
    });
    //
    return () => {
      window.removeEventListener('popstate', () => true);
    };
  }, [rest.open]);

  // after run onDismiss function the isOnDismissCalledStatus state should be false to handle next reopen of action sheet
  useEffect(() => {
    !isOpenActionSheet && setIsOnDismissCalledStatus(false);
  }, [isOpenActionSheet]);

  const defaultFooter = (
    <StyledDiv
      display="grid"
      gridTemplateColumns="1fr 2fr"
      gridColumnGap={5}
      p={5}>
      <Button
        btnType="green"
        onClick={(): void => {
          onDefault();
          navigate(-1);
        }}>
        {t('global.button.default')}
      </Button>
      <Button
        btnType="blue"
        onClick={(): void => {
          onApply();
          navigate(-1);
        }}
        loading={loading}>
        {t('global.button.apply')}
      </Button>
    </StyledDiv>
  );

  return (
    <StyledBottomSheet
      footer={defaultFooter}
      initialFocusRef={false}
      scrollLocking={false}
      expandOnContentDrag={false}
      ref={bottomSheetRef}
      {...rest}
      onDismiss={(): void => {
        // the onDismiss function runs two times when user pulls down the action sheet
        // therefor we should handle it with an if statement

        if (!isOnDismissCalledStatus) {
          navigate(-1);
          setIsOnDismissCalledStatus(true);
        }
      }}
      open={!!isOpenActionSheet && isOpenActionSheet === id}>
      <StyledSheetContainer>{children}</StyledSheetContainer>
    </StyledBottomSheet>
  );
};

export default ActionSheet;
